import { Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { useEffect, useState } from "react"
import { SavingReducer } from "../../../state/reducers/general"
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../state/configureStore'
import { fetchCustomerNodes, moveNode } from "../../../state/thunks/customer"
import { CustomerNodeSummary } from "../pricemodel/types"
import { MOVE_NODE } from '../../../state/actionTypes/customer'
import { SaveModal } from '../../../components/molecules/SavingModal'

type MoveStoreProps = {
    nodeId: string,
    parentNodeId: string | undefined,
    open: boolean,
    closeModal: Function
}

export const MoveStoreModal = (props: MoveStoreProps) => {
    const [savingStatus, setSavingStatus] = useState<SavingReducer>("ready")
    const [customerNodes, setCustomerNodes] = useState<CustomerNodeSummary[]>([])
    const [failedMessage, setFailedMessage] = useState<string>("Could not move store!")
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
    const [parentName, setParentName] = useState<string>("Unknown")
    const [parentId, setParentId] = useState<string>(props.parentNodeId ?? '')
    const [fetchingCustomers, setFetchingCustomers] = useState<boolean>(false)

    const dispatch = useDispatch<AppDispatch>()

    const handleSave = () => {
        setSavingStatus('saving')

        if (props.nodeId && props.parentNodeId && selectedCustomerId) {
            dispatch(moveNode({params : { id: props.nodeId, fromNodeId: props.parentNodeId, toNodeId: selectedCustomerId }}))
                .then(response => {
                    if (response.type === MOVE_NODE + "/rejected") {
                        setFailedMessage("Could not move node! Try again later.")
                        setSavingStatus('failed')
                    } else {
                        setParentName(customerNodes.find(n => n.id === selectedCustomerId)?.name ?? "Unknown")
                        setParentId(selectedCustomerId)
                        setSavingStatus('saved')
                        setSelectedCustomerId('')
                    }
                })
                .catch(reason => {
                    setFailedMessage("An error occured: " + reason)
                    setSavingStatus('failed')
                })
        } else {
            setFailedMessage("An error occured! Try again later.")
            setSavingStatus('failed')
        }
    }

    useEffect(() => {
        setFetchingCustomers(true)
        dispatch(fetchCustomerNodes())
            .unwrap()
            .then(nodes => {
                setParentName(nodes.find(n => n.id === props.parentNodeId)?.name ?? "Unknown")
                const customerNodesFiltered = nodes.filter(c => c.level_name === 'Companies') ?? []
                setCustomerNodes(customerNodesFiltered)
            })
            .catch(() => {
                setFailedMessage("Could not fetch customers!")
                setSavingStatus("failed")
                setCustomerNodes([])
            })
            .finally(() => setFetchingCustomers(false))
    }, [props.nodeId])

    const handleSelectedCustomerChange = (event: SelectChangeEvent<string>) => {
        setSelectedCustomerId(event.target.value)
    }

    return (
        <SaveModal 
            open={props.open} 
            title={`Move store from ${parentName} to`}
            savingFailedMessage={failedMessage}
            closeModal={props.closeModal}
            onSave={handleSave}
            saving={savingStatus === 'saving'}
            savingSuccessMessage={`Store moved to ${parentName} successfully!`}
            saveButtonText="Move store"
            savingFailed={savingStatus === 'failed'}
            savingOngoing={savingStatus === 'saving'}
            savingSuccessful={savingStatus === 'saved'}
            setSavingFailed={(value: any) => {value ? setSavingStatus('failed') : setSavingStatus('saving')}}
            savingDisabled={selectedCustomerId === ''}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '5px', minWidth: '350px' }}>
                {fetchingCustomers && (<CircularProgress/>)}
                <FormControl fullWidth size='small'>
                    <InputLabel id='customer-name-label'>Customer name</InputLabel>
                    <Select
                        labelId='customer-name-label'
                        id='customer-name'
                        value={selectedCustomerId}
                        label='Customer name'
                        onChange={handleSelectedCustomerChange}
                    >
                        {customerNodes.filter(c => c.id !== parentId).map(customer => (
                        <MenuItem key={customer.id} value={customer.id}>
                            {customer.name}
                        </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </SaveModal>
    )
}
